import React, { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { Phone, Mail, MapPin, Snowflake } from "lucide-react";
import api from "@/lib/api";
import StatusPill from "@/components/StatusPill";

const fmt = (iso) =>
  new Date(iso).toLocaleDateString(undefined, { day: "numeric", month: "short", year: "numeric" });

export default function ClientDetail() {
  const { clientId } = useParams();
  const [data, setData] = useState(null);

  useEffect(() => {
    api.get(`/clients/${clientId}`).then((r) => setData(r.data));
  }, [clientId]);

  if (!data) return <div className="text-[#6A6E85]">Loading…</div>;
  const { client, sites, assets, jobs } = data;

  const assetsBySite = sites.map((s) => ({
    site: s,
    assets: assets.filter((a) => a.site_id === s.id),
  }));

  return (
    <div className="space-y-6">
      <div>
        <Link to="/office/clients" className="text-[#6A6E85] hover:text-[#181854] text-sm">
          ← Clients
        </Link>
        <h1 className="text-3xl font-bold text-[#181854] tracking-tight mt-1">{client.name}</h1>
        <div className="mt-2 flex flex-wrap gap-4 text-sm text-[#6A6E85]">
          {client.phones?.[0] && <span className="flex items-center gap-1.5"><Phone size={13} /> {client.phones[0]}</span>}
          {client.emails?.[0] && <span className="flex items-center gap-1.5"><Mail size={13} /> {client.emails[0]}</span>}
          {client.billing_ref && <span>Ref: {client.billing_ref}</span>}
        </div>
      </div>

      <div className="grid lg:grid-cols-2 gap-6">
        <div className="bg-white rounded-xl border border-[#E4E6EF] p-5 shadow-sm">
          <h2 className="text-lg font-semibold text-[#181854]">Sites & assets</h2>
          <div className="mt-3 space-y-4">
            {assetsBySite.map(({ site, assets: list }) => (
              <div key={site.id} className="border border-[#E4E6EF] rounded-lg p-3">
                <div className="flex items-center gap-2 text-sm font-medium text-[#14162E]">
                  <MapPin size={14} /> {site.address}
                </div>
                {site.access_notes && (
                  <p className="text-xs text-[#6A6E85] mt-1">{site.access_notes}</p>
                )}
                <ul className="mt-3 space-y-1.5">
                  {list.map((a) => (
                    <li key={a.id} className="flex items-center justify-between text-sm bg-[#F5F6FA] rounded-md px-3 py-2">
                      <span className="flex items-center gap-2 text-[#14162E]">
                        <Snowflake size={13} className="text-[#4F9CE0]" />
                        {a.label}
                        <span className="text-xs text-[#6A6E85]">({a.type})</span>
                      </span>
                      <span className="text-xs text-[#6A6E85]">
                        Installed {fmt(a.install_date)}
                      </span>
                    </li>
                  ))}
                  {list.length === 0 && (
                    <li className="text-xs text-[#6A6E85]">No assets recorded.</li>
                  )}
                </ul>
              </div>
            ))}
          </div>
        </div>

        <div className="bg-white rounded-xl border border-[#E4E6EF] p-5 shadow-sm">
          <h2 className="text-lg font-semibold text-[#181854]">Service history</h2>
          <ul className="mt-3 divide-y divide-[#E4E6EF]">
            {jobs.map((j) => (
              <li key={j.id}>
                <Link
                  to={`/office/jobs/${j.id}`}
                  className={`block py-3 pl-3 pr-2 hover:bg-[#F5F6FA] rounded-lg transition-colors duration-200 status-border-${j.status}`}
                >
                  <div className="flex items-center justify-between gap-3">
                    <div className="min-w-0">
                      <p className="font-medium text-[#14162E] truncate">{j.title}</p>
                      <p className="text-xs text-[#6A6E85]">{fmt(j.scheduled_at)}</p>
                    </div>
                    <StatusPill status={j.status} />
                  </div>
                </Link>
              </li>
            ))}
            {jobs.length === 0 && (
              <li className="text-sm text-[#6A6E85] py-4 text-center">No service history yet.</li>
            )}
          </ul>
        </div>
      </div>
    </div>
  );
}
